/** Maximum size of a serialized chart (song txt) that can be transferred to the room.
 * The biggest chart in the game is ~30 KB raw (a few KB gzipped), so this is generous. */
export const ONLINE_MAX_CHART_BYTES = 512 * 1024;

/** How often the host broadcasts a heartbeat. Every client watches this: it is the only signal
 * that distinguishes a room where nothing is happening from a host whose tab got throttled into
 * the background, and the room's authority now lives in a browser that can do exactly that. */
export const ONLINE_HOST_HEARTBEAT_MS = 1_000;

/** No heartbeat for this long and the host is presumed gone — the succession path starts. Several
 * beats' worth, because a foregrounded tab under load (pitch detection, video, rendering) can miss
 * one without being in any trouble. */
export const ONLINE_HOST_STALL_MS = 4_000;

/** How often the host rebroadcasts its snapshot for the succession line. Slow on purpose: it only
 * has to be fresh enough that a takeover is not visibly behind, and everything in it that changes
 * fast (the leaderboard, player stats) is republished on its own channel anyway. */
export const ONLINE_SNAPSHOT_BROADCAST_MS = 2_000;

/** Clients do not all rush the promotion endpoint at once: each waits its rank in the succession
 * order times this before claiming. The obvious successor lands first and everyone else's claim
 * comes back as a stale epoch, which is also how they learn who won. */
export const ONLINE_PROMOTE_STAGGER_MS = 750;

/** How long a disconnected participant keeps their spot (and the host their role) before removal. */
export const ONLINE_RECONNECT_GRACE_MS = 15_000;

/** Idle room time-to-live — storage is wiped this long after the last activity. Short because an
 * abandoned room now goes quiet (see ONLINE_IDLE_AFTER_MS) instead of holding the party awake, so
 * there is nothing to gain from keeping a dead room's snapshot around for hours. */
export const ONLINE_ROOM_TTL_MS = 30 * 60 * 1_000;

/** How long a singer can go without moving the pointer or pressing a key before their client stops
 * reporting stats and pinging altogether. Only applies in the lobby and on the results screen —
 * nobody touches the controls while a countdown is running or a song is playing. Long enough not to
 * blink out on someone reading the song list, short enough that a forgotten tab stops holding the
 * room (and its Durable Object duration bill) awake. */
export const ONLINE_IDLE_AFTER_MS = 30 * 1_000;

/** How long a singer can report buffering before the room auto-pauses for everyone. */
export const ONLINE_BUFFERING_PAUSE_MS = 3_500;

/** Buffering this soon after a client seeks its own video is caused by the seek, so it is only
 * reported to the room once it outlasts this window — otherwise every resume could re-pause the room. */
export const ONLINE_SEEK_SETTLE_MS = 5_500;

/** How long the room waits for everyone to confirm readiness before starting the song anyway —
 * the same autostart the local game gives its remote mics. */
export const ONLINE_READINESS_TIMEOUT_MS = 15_000;

/** Lead time between the room deciding to start and the video actually rolling. Long enough for the
 * readiness screen to show its last tick and bow out, rather than being cut off the instant the
 * final singer confirms — and it doubles as the window every client schedules its play() in. */
export const ONLINE_START_LEAD_MS = 2_000;

/** Short countdown before resuming after a pause. */
export const ONLINE_RESUME_COUNTDOWN_MS = 3_000;

/** Clients seek when their playback drifts further than this from the room's expected position. */
export const ONLINE_DRIFT_THRESHOLD_MS = 2_000;

/** Leaderboard broadcasts are coalesced so subscribers get at most one update per this interval. */
export const ONLINE_LEADERBOARD_PUBLISH_MS = 500;

/** Player-stats (ping/volume) broadcasts are coalesced to at most one per this interval. Fast
 * enough for the volume bars to read as live meters (~3 updates/s) rather than a slideshow — the
 * payload is a couple of numbers per singer, and silent singers stop reporting altogether. */
export const ONLINE_STATS_PUBLISH_MS = 300;

/** How often each singer samples their mic volume and reports it (with their ping) to the room.
 * Matches the broadcast interval — sampling faster would only be coalesced away. */
export const ONLINE_STATS_REPORT_MS = 300;

/** How long the room waits for final scores after the host ends the game before forcing results. */
export const ONLINE_FORCE_RESULTS_MS = 5_000;

/** Online rooms are for singing together — a lone singer burns the room's duration bill on a
 * solo they could sing locally for free, so the host can't start until someone else is in. */
export const ONLINE_MIN_PLAYERS = 2;

export const ONLINE_ROOM_CODE_LENGTH = 5;

/** Longest name a participant can set for themselves (matches the local game's MAX_NAME_LENGTH). */
export const ONLINE_MAX_NAME_LENGTH = 20;

/** Most samples a published score timeline may hold. The game sends `SCORE_TIMELINE_SAMPLES + 1`;
 * this only has to stop a client that isn't ours from bloating every room-state publish. */
export const ONLINE_MAX_SCORE_TIMELINE_SAMPLES = 241;

/** Supported range for the pitch-matching tolerance — same scale as the local game's difficulty
 * picker (1 = Hard .. 3 = Easy in production, up to 6 for the dev-only debug difficulties). */
export const ONLINE_MIN_TOLERANCE = 1;
export const ONLINE_MAX_TOLERANCE = 6;

/** Longest chat message a singer can send. The input caps typing at this, so the room-side limit
 * only ever fires on a client that isn't ours. */
export const ONLINE_MAX_CHAT_LENGTH = 200;

/** How many messages the room keeps. Everything older is dropped — the panel has no scrollback,
 * and the whole history rides the host's snapshot to whoever takes the room over next. */
export const ONLINE_CHAT_HISTORY_SIZE = 100;

/** Sustained chat rate per singer: the window, and how many messages fit in it. */
export const ONLINE_CHAT_RATE_WINDOW_MS = 60 * 1_000;
export const ONLINE_CHAT_RATE_LIMIT = 40;

/** Burst guard on top of the sustained limit — 40-per-minute on its own permits all forty inside
 * two seconds, which is the shape spam actually takes. */
export const ONLINE_CHAT_BURST_WINDOW_MS = 5 * 1_000;
export const ONLINE_CHAT_BURST_LIMIT = 5;

/** The rate limiter's rejection, spelled once so the client can tell it apart from a message that
 * failed because the room was mid-handover — the two want different wording and only one of them
 * is worth offering a resend for. It travels as a plain string (that is all an RPC error is), so
 * both ends have to name the same constant. */
export const ONLINE_CHAT_RATE_LIMIT_ERROR = 'Slow down — too many messages';

/** How often chat writes the room snapshot. Chat deliberately does not persist per message — the
 * whole history is in the snapshot, and in a P2P room persisting broadcasts that snapshot to the
 * succession line, so a write per message would put a hundred lines on the wire for every one
 * sent. It cannot skip persisting altogether either: nothing else writes the snapshot on a timer,
 * so an acknowledged message would be lost if the host vanished before the next persist. Coalescing
 * at this interval bounds both — at worst a takeover loses this much of the conversation. */
export const ONLINE_CHAT_PERSIST_MS = 2_000;
